'use client'

import Link from 'next/link'
import styled from 'styled-components'

import { colors } from '../../../../shared/constants/colors'
import { CategoryListHeader } from '../../../../shared/ui-kit/actual/headers/CategoryListHeader'
import { MainHeader } from '../../../../shared/ui-kit/actual/headers/MainHeader'
import {
  breakpoints,
  mobileTabletView,
  tabletDesktopView,
} from '../../../../shared/ui-kit/breakPoints'
import { generatePagesArray } from '../../../../shared/utils/paginationHelper'

type CategoryItem = {
  categoryId: number
  categoryName: string
  hasChildCategories: boolean
  imageUrl?: null | string
}

interface ParentCategoryPageViewProps {
  items: CategoryItem[]
  pageNumber: number
  parentCategoryId: string
  parentCategoryName: string
  sectionId: string
  sectionName: string
  totalPages: number
}

const BasePage = styled.main`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 0 12px 60px;
  width: clamp(${breakpoints.mobileMin}, 100%, ${breakpoints.desktopMax});
  margin: 0 auto;
  @media (width >= ${breakpoints.tabletMin}) {
    padding: 0 36px 60px;
    overflow: auto;
  }
`

const DesktopHeaderWrapper = styled.div`
  display: none;
  width: 100%;

  @media (width >= ${breakpoints.productCardsDesktopMin}) {
    display: block;
  }
`

const BaseWrapper = styled.div`
  width: 500px;
  width: 100%;
  padding: 0 36px;
  @media screen and (width <= ${breakpoints.tabletMin}) {
    padding: 0;
  }
`

const FlexColumn = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`

const Breadcrumbs = styled.nav`
  font-size: 18px;
  color: #000;
  margin: 32px 0 24px;
  width: 100%;

  a {
    color: #000;
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }

  ${tabletDesktopView}
`

const BlockTitle = styled.h2`
  width: 100%;
  margin: 0 0 16px;
  font-weight: 700;
`

const CategoriesWrapper = styled.div`
  min-height: 580px;
  display: flex;
  flex-wrap: wrap;
  gap: 16px 16px;
  transition: all 0.3s ease-in;
  justify-content: flex-start;
  align-self: flex-start;
  @media all and (width <= ${breakpoints.tabletMax}) {
    justify-content: center;
    align-self: center;
  }
`

const CategoryBlock = styled.div`
  width: 167px;
  height: 201px;
`

const CategoryCard = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  height: 100%;
  padding: 6px;
  border-radius: 12px;
  background: ${colors.lightGray};
  box-sizing: border-box;
  text-decoration: none;
  color: ${colors.black};
`

const CategoryMedia = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 149px;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 10px;
  background: ${colors.white};
`

const CategoryImage = styled.img`
  width: 104px;
  height: 74px;
  object-fit: contain;
`

const CategoryImageFallback = styled.div`
  width: 104px;
  height: 74px;
  border-radius: 10px;
  background: ${colors.lightGray};
`

const CategoryName = styled.p`
  width: 100%;
  text-align: left;
  margin: 0;
  padding: 0 2px 2px 0;
  color: ${colors.black};
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.22px;
  overflow-wrap: anywhere;
`

const EmptyText = styled.p`
  width: 100%;
  margin: 0;
`

const PaginationWrapper = styled.div`
  margin-top: 32px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;

  @media all and (max-width: 800px) {
    margin-bottom: 35px;
  }
`

const PageButton = styled(Link)<{ $active: boolean }>`
  margin-top: 20px;
  background: ${({ $active }) => ($active ? colors.primary : colors.lightGray)};
  color: ${({ $active }) => ($active ? colors.white : colors.black)};
  min-width: 41px;
  height: 41px;
  border: none;
  cursor: pointer;
  font-size: 16px;
  border-radius: 4px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
`

const BottomMenu = styled.nav`
  position: fixed;
  bottom: -1px;
  left: 0px;
  margin: 0;
  border-top: 1px solid ${colors.lightGray};
  background-color: white;
  width: 100vw;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 30px 30px 0px 0px;
  z-index: 2000;
  box-shadow: 0px -5px 60px 0px rgba(0, 0, 0, 0.05);
  ${mobileTabletView}
`

const BottomMenuInner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  padding: 19px 37px 20px 37px;
`

const BottomItem = styled(Link)<{ $active?: boolean }>`
  display: flex;
  flex-direction: column;
  height: 40px;
  width: 40px;
  row-gap: 4px;
  justify-content: center;
  align-items: center;
  text-decoration: none;

  img,
  svg {
    width: 20px;
    height: 20px;
  }

  p {
    color: ${({ $active }) => ($active ? colors.primary : colors.grey)};
    font-size: 10px;
    font-style: normal;
    font-weight: 600;
    line-height: 150%;
    margin: 0;
  }
`

export const ParentCategoryPageView = ({
  items,
  pageNumber,
  parentCategoryId,
  parentCategoryName,
  sectionId,
  sectionName,
  totalPages,
}: ParentCategoryPageViewProps) => {
  const pages = generatePagesArray(totalPages, pageNumber, 100)

  return (
    <BasePage>
      <DesktopHeaderWrapper>
        <MainHeader />
      </DesktopHeaderWrapper>
      <BaseWrapper>
        <FlexColumn style={{ width: '100%' }}>
          <CategoryListHeader backHref={`/sections/${sectionId}`} title={parentCategoryName} />

          <Breadcrumbs>
            <Link href={'/'}>Главная</Link>
            {' / '}
            <Link href={'/sections'}>Разделы</Link>
            {' / '}
            <Link href={`/sections/${sectionId}`}>{sectionName}</Link>
            {' / '}
            <span>{parentCategoryName}</span>
          </Breadcrumbs>

          <BlockTitle>Категории</BlockTitle>

          <CategoriesWrapper>
            {items.length > 0
              ? items.map(category => {
                  const href = category.hasChildCategories
                    ? `/sections/${sectionId}/${parentCategoryId}/${category.categoryId}`
                    : `/sections/${sectionId}/${parentCategoryId}/sub/${category.categoryId}`

                  return (
                    <CategoryBlock key={category.categoryId}>
                      <CategoryCard href={href}>
                        <CategoryMedia>
                          {category.imageUrl ? (
                            <CategoryImage alt={category.categoryName} src={category.imageUrl} />
                          ) : (
                            <CategoryImageFallback />
                          )}
                        </CategoryMedia>
                        <CategoryName>{category.categoryName}</CategoryName>
                      </CategoryCard>
                    </CategoryBlock>
                  )
                })
              : null}
          </CategoriesWrapper>

          {items.length === 0 ? (
            <EmptyText>В этом разделе пусто, попробуйте другой</EmptyText>
          ) : null}

          {totalPages > 1 ? (
            <PaginationWrapper>
              {pages.map(page => (
                <PageButton
                  $active={page - 1 === pageNumber}
                  href={
                    page === 1
                      ? `/sections/${sectionId}/${parentCategoryId}`
                      : `/sections/${sectionId}/${parentCategoryId}?page=${page - 1}`
                  }
                  key={page}
                >
                  {page}
                </PageButton>
              ))}
            </PaginationWrapper>
          ) : null}
        </FlexColumn>
        <BottomMenu aria-label={'Нижнее меню'}>
          <BottomMenuInner>
            <BottomItem href={'/orders'}>
              <img alt={''} src={'/images/bottom-menu/cart-gray.svg'} />
              <p>Заказы</p>
            </BottomItem>
            <BottomItem href={'/messages'}>
              <img alt={''} src={'/images/bottom-menu/chat-gray.svg'} />
              <p>Чат</p>
            </BottomItem>
            <BottomItem href={'/blog'}>
              <img alt={''} src={'/images/bottom-menu/blog-gray.svg'} />
              <p>Блог</p>
            </BottomItem>
            <BottomItem href={'/'}>
              <img alt={''} src={'/images/bottom-menu/main-gray.svg'} />
              <p>Главная</p>
            </BottomItem>
            <BottomItem href={'/profile'}>
              <img alt={''} src={'/images/bottom-menu/profile-gray.svg'} />
              <p>Профиль</p>
            </BottomItem>
          </BottomMenuInner>
        </BottomMenu>
      </BaseWrapper>
    </BasePage>
  )
}
